import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Download, FileText, X } from "lucide-react";
import type { Attachment } from "@kandoo/shared";
import { attachmentUrl, attachmentDownloadUrl } from "../api/client";
import { Markdown } from "./Markdown";
import { Modal } from "./Modal";

interface AttachmentViewerProps {
  attachments: Attachment[];
  /** Id of the attachment to open first. */
  attachmentId: string;
  onClose: () => void;
}

/** Anything bigger than this is not worth pulling into the browser to preview. */
const MAX_TEXT_PREVIEW = 1_000_000;

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

const isImage = (mime: string) => mime.startsWith("image/");
const isVideo = (mime: string) => mime.startsWith("video/");
const isAudio = (mime: string) => mime.startsWith("audio/");

// Files whose mime the server didn't recognise (application/octet-stream and
// friends) but which are plainly text once you look at the name.
const TEXT_EXTENSIONS = [
  "txt",
  "md",
  "markdown",
  "log",
  "csv",
  "tsv",
  "json",
  "jsonl",
  "xml",
  "yaml",
  "yml",
  "toml",
  "ini",
  "conf",
  "env",
  "css",
  "scss",
  "html",
  "js",
  "jsx",
  "ts",
  "tsx",
  "py",
  "rb",
  "go",
  "rs",
  "java",
  "c",
  "h",
  "cpp",
  "cs",
  "php",
  "sh",
  "bash",
  "zsh",
  "sql",
  "diff",
  "patch",
];

function isTextish(a: Attachment): boolean {
  const mime = a.mime.toLowerCase();
  if (
    mime.startsWith("text/") ||
    mime === "application/json" ||
    mime === "application/xml" ||
    mime === "application/javascript" ||
    mime.endsWith("+json") ||
    mime.endsWith("+xml")
  ) {
    return true;
  }
  const ext = a.filename.toLowerCase().split(".").pop() ?? "";
  return TEXT_EXTENSIONS.includes(ext) && a.filename.includes(".");
}

// mdx is close enough to md for a preview — we render it as markdown.
const MARKDOWN_EXTENSIONS = ["md", "markdown", "mdx"];

/** A narrower check on top of isTextish: text we can render rather than dump. */
function isMarkdown(a: Attachment): boolean {
  const mime = a.mime.toLowerCase();
  if (mime === "text/markdown" || mime === "text/x-markdown") return true;
  const ext = a.filename.toLowerCase().split(".").pop() ?? "";
  return MARKDOWN_EXTENSIONS.includes(ext) && a.filename.includes(".");
}

/** Full-size preview of one attachment, with paging through the card's set. */
export function AttachmentViewer({
  attachments,
  attachmentId,
  onClose,
}: AttachmentViewerProps) {
  const initial = Math.max(
    0,
    attachments.findIndex((a) => a.id === attachmentId),
  );
  const [index, setIndex] = useState(initial);

  const current = attachments[Math.min(index, attachments.length - 1)];

  const [text, setText] = useState<string | null>(null);
  const [textError, setTextError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // The browser couldn't decode this media (unsupported codec) — show the
  // download panel instead of a broken player.
  const [playbackFailed, setPlaybackFailed] = useState(false);
  // Markdown previews start rendered; the raw source is one click away.
  const [rawMarkdown, setRawMarkdown] = useState(false);

  const showText = current ? isTextish(current) : false;
  const showMarkdown = current ? isMarkdown(current) : false;
  const tooLarge = current ? current.size > MAX_TEXT_PREVIEW : false;
  const id = current?.id;

  // A new attachment gets a fresh shot at playing.
  useEffect(() => {
    setPlaybackFailed(false);
  }, [id]);

  // …and a fresh view mode.
  useEffect(() => {
    setRawMarkdown(false);
  }, [id]);

  useEffect(() => {
    setText(null);
    setTextError(null);
    if (!id || !showText || tooLarge) return;
    let cancelled = false;
    setLoading(true);
    fetch(attachmentUrl(id), { credentials: "same-origin" })
      .then((res) => {
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
        return res.text();
      })
      .then((body) => {
        if (!cancelled) setText(body);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setTextError(e instanceof Error ? e.message : "Failed to load file");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, showText, tooLarge]);

  // Nothing left to show (e.g. the attachment was deleted underneath us).
  useEffect(() => {
    if (attachments.length === 0) onClose();
  }, [attachments.length, onClose]);

  if (!current) return null;

  const many = attachments.length > 1;
  const step = (delta: number) =>
    setIndex((i) => (i + delta + attachments.length) % attachments.length);

  const noPreview = (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 p-10 text-center">
      <FileText size={40} className="text-slate-400" />
      <div className="max-w-full truncate text-sm font-medium text-slate-800">
        {current.filename}
      </div>
      <div className="text-xs text-slate-500">
        {current.mime} · {formatBytes(current.size)}
      </div>
      <p className="mt-1 text-sm text-slate-500">
        No preview available — use Download.
      </p>
    </div>
  );

  const playable = !playbackFailed;

  return (
    <Modal onClose={onClose} ariaLabel="Attachment preview" size="xl" fitHeight>
      <div className="flex min-h-0 flex-1 flex-col">
        {/* Header (fixed) */}
        <div className="flex shrink-0 items-center gap-3 border-b border-slate-200 px-5 py-3">
          <div className="min-w-0 flex-1">
            <h2
              className="truncate font-semibold text-slate-800"
              title={current.filename}
            >
              {current.filename}
            </h2>
            <div className="text-xs text-slate-500">
              {formatBytes(current.size)} · {current.mime}
              {many ? ` · ${index + 1} of ${attachments.length}` : ""}
            </div>
          </div>
          {showMarkdown && !tooLarge && (
            <div className="flex shrink-0 items-center gap-0.5 rounded bg-slate-100 p-0.5">
              {([false, true] as const).map((raw) => (
                <button
                  key={raw ? "raw" : "rendered"}
                  type="button"
                  onClick={() => setRawMarkdown(raw)}
                  aria-pressed={rawMarkdown === raw}
                  className={`rounded px-2 py-1 text-sm ${
                    rawMarkdown === raw
                      ? "bg-[var(--surface)] font-medium text-slate-900 shadow-sm"
                      : "text-slate-500 hover:text-slate-800"
                  }`}
                >
                  {raw ? "Raw" : "Rendered"}
                </button>
              ))}
            </div>
          )}
          <a
            href={attachmentDownloadUrl(current.id)}
            download
            className="flex shrink-0 items-center gap-1.5 rounded bg-slate-100 px-2.5 py-1.5 text-sm text-slate-700 hover:bg-slate-200"
          >
            <Download size={14} />
            Download
          </a>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body (scrolls) */}
        <div className="relative flex min-h-0 flex-1 flex-col">
          {many && (
            <>
              <button
                type="button"
                onClick={() => step(-1)}
                aria-label="Previous attachment"
                className="absolute top-1/2 left-2 z-10 -translate-y-1/2 rounded-full bg-[var(--surface)]/90 p-2 text-slate-600 shadow hover:text-slate-900"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                type="button"
                onClick={() => step(1)}
                aria-label="Next attachment"
                className="absolute top-1/2 right-2 z-10 -translate-y-1/2 rounded-full bg-[var(--surface)]/90 p-2 text-slate-600 shadow hover:text-slate-900"
              >
                <ChevronRight size={18} />
              </button>
            </>
          )}

          {isImage(current.mime) ? (
            <div className="themed-scroll flex min-h-0 flex-1 items-center justify-center overflow-auto bg-slate-800 p-4">
              <img
                src={attachmentUrl(current.id)}
                alt={current.filename}
                className="mx-auto max-h-full max-w-full object-contain"
              />
            </div>
          ) : isVideo(current.mime) && playable ? (
            <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden bg-slate-800 p-4">
              <video
                key={current.id}
                src={attachmentUrl(current.id)}
                controls
                preload="metadata"
                onError={() => setPlaybackFailed(true)}
                className="mx-auto max-h-full max-w-full"
              />
            </div>
          ) : isAudio(current.mime) && playable ? (
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 p-10 text-center">
              <FileText size={40} className="text-slate-400" />
              <div className="max-w-full truncate text-sm font-medium text-slate-800">
                {current.filename}
              </div>
              <div className="text-xs text-slate-500">
                {current.mime} · {formatBytes(current.size)}
              </div>
              <audio
                key={current.id}
                src={attachmentUrl(current.id)}
                controls
                preload="metadata"
                onError={() => setPlaybackFailed(true)}
                className="mt-3 w-full"
              />
            </div>
          ) : showText ? (
            <div className="themed-scroll min-h-0 flex-1 overflow-auto p-4">
              {tooLarge ? (
                <p className="text-sm text-slate-500">
                  Too large to preview ({formatBytes(current.size)}) — download
                  instead.
                </p>
              ) : loading ? (
                <p className="text-sm text-slate-500">Loading…</p>
              ) : textError ? (
                <p className="text-sm text-red-600">
                  Could not load preview: {textError}
                </p>
              ) : showMarkdown && !rawMarkdown ? (
                <Markdown>{text ?? ""}</Markdown>
              ) : (
                <pre className="themed-scroll overflow-auto rounded-lg border border-slate-200 bg-slate-100 p-3 font-mono text-xs whitespace-pre text-slate-800">
                  {text}
                </pre>
              )}
            </div>
          ) : (
            noPreview
          )}
        </div>
      </div>
    </Modal>
  );
}
